import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Link, Stack, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { CredentialField } from './CredentialField';
import { useLocalization } from '@/hooks/useLocalization';
import {
  AggregatorCredentialsFormType,
  AggregatorFormStatus,
  isEditableFieldType,
  type AggregatorCredentialsModel,
} from '@/utils/aggregatorCredentials';

interface AggregatorCredentialsFormProps {
  model: AggregatorCredentialsModel;
  institutionName: string;
  onSubmit: (model: AggregatorCredentialsModel) => void;
  isSubmitting?: boolean;
  institutionUrl?: string | null;
  onCancel?: () => void;
}

/** Legacy `getTitle` (CredentialsForm.jsx:168-179) mixed strings and numbers, so its
 * string cases never matched. `Type` is numeric on the wire — switch on the enum. */
function titleKeyFor(type: AggregatorCredentialsFormType): string {
  switch (type) {
    case AggregatorCredentialsFormType.AddCredentials:
    case AggregatorCredentialsFormType.UpdateCredentials:
      return 'connectionError.credentials.loginTitle';
    case AggregatorCredentialsFormType.MfaCredentials:
    case AggregatorCredentialsFormType.ManualMfaCredentials:
      return 'connectionError.credentials.mfaTitle';
    default:
      return 'connectionError.credentials.genericTitle';
  }
}

/**
 * Renders whatever credential form the aggregator asked for, and hands the filled
 * model back to the caller. Aggregator-agnostic by design: this component never
 * learns which aggregator it is serving.
 */
export const AggregatorCredentialsForm: React.FC<
  AggregatorCredentialsFormProps
> = ({
  model,
  institutionName,
  onSubmit,
  isSubmitting = false,
  institutionUrl,
  onCancel,
}) => {
  const { t } = useLocalization();
  const form = model.CredentialsForm;
  const fields = form.CredentialFormFields ?? [];

  // Only editable fields get local state. Image/link fields carry a backend
  // `Value` that must round-trip untouched, so seeding them with '' would wipe it.
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      fields
        .filter((field) => isEditableFieldType(field.FieldType))
        .map((field) => [field.FieldName, field.Value ?? '']),
    ),
  );

  const sanitizedInstructions = useMemo(
    () =>
      form.Instructions
        ? DOMPurify.sanitize(form.Instructions, {
            USE_PROFILES: { html: true },
          })
        : '',
    [form.Instructions],
  );

  const handleChange = (fieldName: string, value: string): void => {
    setValues((current) => ({ ...current, [fieldName]: value }));
  };

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();

    // Clone rather than mutate: this model comes from the RTK Query cache, which
    // Immer freezes. Legacy mutated in place because it had no such constraint.
    onSubmit({
      ...model,
      CredentialsForm: {
        ...form,
        CredentialFormFields: fields.map((field) => ({
          ...field,
          Value: values[field.FieldName] ?? field.Value,
        })),
      },
    });
  };

  const failed = form.Status.StatusCode === AggregatorFormStatus.Failure;

  // Legacy hardcoded `required: true` on every field (CredentialsForm.jsx:128) and
  // gated Submit on it. The contract has no per-field requiredness, so keep parity.
  const canSubmit = Object.values(values).every((value) => value.trim() !== '');

  return (
    <Box
      component='form'
      onSubmit={handleSubmit}
    >
      <Typography
        gutterBottom
        variant='h6'
      >
        {t(titleKeyFor(form.Type))}
      </Typography>

      {failed && (
        <Alert
          severity='error'
          sx={{ mb: 2 }}
        >
          {form.Status.Message ??
            t('connectionError.credentials.invalidCredentials')}
          {institutionUrl ? (
            <Box sx={{ mt: 1 }}>
              <Link
                href={institutionUrl}
                rel='noopener'
                target='_blank'
              >
                {t('connectionError.credentials.verifyAtInstitution', {
                  name: institutionName,
                })}
              </Link>
            </Box>
          ) : null}
        </Alert>
      )}

      {sanitizedInstructions ? (
        <Typography
          color='text.secondary'
          dangerouslySetInnerHTML={{ __html: sanitizedInstructions }}
          variant='body2'
        />
      ) : null}

      {fields.map((field) => (
        <CredentialField
          field={field}
          key={field.FieldName}
          onChange={handleChange}
          value={values[field.FieldName] ?? ''}
        />
      ))}

      <Stack
        direction='row'
        spacing={1}
        sx={{ mt: 2 }}
      >
        <Button
          disabled={isSubmitting || !canSubmit}
          type='submit'
          variant='contained'
        >
          {t('connectionError.credentials.submit')}
        </Button>
        {onCancel ? (
          <Button
            disabled={isSubmitting}
            onClick={onCancel}
            variant='text'
          >
            {t('connectionError.credentials.back')}
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
};
